import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, Pencil, Trash2, MapPin, Calendar } from "lucide-react";
import { useApi, errMsg, fmtDate, fmtDateTime, money } from "@/lib/api";
import { useMe } from "@/components/portal/MeContext";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/portal/StatusBadge";
import { ProjectForm } from "@/components/portal/ProjectForm";
import { MessageThread } from "@/components/portal/MessageThread";
import { UpdateComposer } from "@/components/portal/UpdateComposer";
import { TYPE_LABEL } from "@/components/portal/ProjectCard";
import { imgUrl } from "@/components/site/WorkGallery";

const Milestones = ({ p, canEdit, onToggle }) => (
  <div className="rounded-2xl border border-[#E6E2DA] bg-white p-5" data-testid="milestones-card">
    <p className="eyebrow mb-3">Milestones</p>
    {!p.milestones?.length && <p className="text-sm text-[#5C5850]">No milestones set.</p>}
    {p.milestones?.map((m) => (
      <button key={m.id} disabled={!canEdit} onClick={() => onToggle(m.id)} data-testid={`milestone-${m.id}`} className="flex w-full items-center gap-3 py-1.5 text-left text-sm disabled:cursor-default">
        {m.done ? <CheckCircle2 size={18} className="text-green-600" /> : <Circle size={18} className="text-[#C9C4BA]" />}
        <span className={m.done ? "text-[#8C877D] line-through" : "font-medium"}>{m.title}</span>
      </button>
    ))}
  </div>
);

const Timeline = ({ updates }) => (
  <div className="rounded-2xl border border-[#E6E2DA] bg-white p-5" data-testid="updates-feed">
    <p className="eyebrow mb-4">Project updates</p>
    {!updates.length && <p className="text-sm text-[#5C5850]">No updates posted yet.</p>}
    <ol className="relative space-y-6 border-l border-[#E6E2DA] pl-6">
      {updates.map((u) => (
        <li key={u.id} className="relative" data-testid={`update-${u.id}`}>
          <span className="absolute -left-[31px] top-1 h-3 w-3 rounded-full border-2 border-white bg-[#F2B91C]" />
          <p className="text-xs text-[#8C877D]">{fmtDateTime(u.created_at)} · {u.author_name}{u.progress != null ? ` · ${u.progress}%` : ""}</p>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{u.message}</p>
          {u.photo_ids?.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid={`update-photos-${u.id}`}>
              {u.photo_ids.map((pid) => (
                <a key={pid} href={imgUrl(pid)} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-lg">
                  <img src={imgUrl(pid)} alt="Job site" loading="lazy" className="aspect-square w-full object-cover transition-transform hover:scale-105" />
                </a>
              ))}
            </div>
          )}
        </li>
      ))}
    </ol>
  </div>
);

export default function ProjectDetail() {
  const { id } = useParams();
  const api = useApi();
  const me = useMe();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [edit, setEdit] = useState(false);
  const staff = me.role !== "customer";

  useEffect(() => {
    api("get", `/projects/${id}`).then(setP).catch(() => setP(false));
  }, [api, id]);

  if (p === false) return <p data-testid="project-not-found">Project not found. <Link to="/portal/projects" className="text-[#A67C00]">Back</Link></p>;
  if (!p) return null;

  const toggle = async (mid) => {
    const r = await api("post", `/projects/${id}/milestones/${mid}/toggle`);
    setP({ ...p, milestones: r.milestones });
  };
  const remove = async () => {
    if (!window.confirm("Delete this project and its updates/messages?")) return;
    try {
      await api("delete", `/projects/${id}`);
      toast.success("Project deleted");
      nav("/portal/projects");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="project-detail-page">
      <Link to="/portal/projects" className="text-sm font-semibold text-[#A67C00]" data-testid="back-to-projects">← All projects</Link>
      <div className="mt-4 flex flex-col gap-4 rounded-2xl bg-[#0E1F3F] p-6 text-white sm:p-8 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="eyebrow !text-[#F2B91C]">{TYPE_LABEL[p.project_type]}</p>
          <h1 className="font-display mt-1 text-3xl font-bold sm:text-4xl" data-testid="project-title">{p.name}</h1>
          <div className="mt-3 flex flex-wrap gap-4 text-sm text-white/70">
            {p.address && <span className="flex items-center gap-1"><MapPin size={14} />{p.address}</span>}
            <span className="flex items-center gap-1"><Calendar size={14} />{fmtDate(p.start_date)} → {fmtDate(p.end_date)}</span>
            {staff && p.budget > 0 && <span className="font-mono-num">Budget {money(p.budget)}</span>}
          </div>
        </div>
        <div className="w-full max-w-xs">
          <div className="mb-2 flex items-center justify-between"><span className="rounded-full bg-white"><StatusBadge status={p.status} testId="project-status-badge" /></span><span className="font-mono-num text-2xl font-semibold" data-testid="project-progress">{p.progress}%</span></div>
          <Progress value={p.progress} className="h-2.5 bg-white/15 [&>div]:bg-[#F2B91C]" />
          {me.role === "admin" && (
            <div className="mt-4 flex gap-2">
              <button onClick={() => setEdit(true)} data-testid="edit-project-btn" className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-semibold text-[#0E1F3F]"><Pencil size={13} />Edit</button>
              <button onClick={remove} data-testid="delete-project-btn" className="inline-flex items-center gap-1.5 rounded-full border border-white/30 px-4 py-2 text-xs font-semibold"><Trash2 size={13} />Delete</button>
            </div>
          )}
        </div>
      </div>
      {p.description && <p className="mt-6 max-w-3xl leading-relaxed text-[#4A4742]">{p.description}</p>}
      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-3">
          {staff && <UpdateComposer project={p} onPosted={(u, ch) => setP({ ...p, ...ch, updates: [u, ...p.updates] })} />}
          <Timeline updates={p.updates} />
        </div>
        <div className="space-y-6 lg:col-span-2">
          <Milestones p={p} canEdit={staff} onToggle={toggle} />
          <MessageThread projectId={p.id} />
        </div>
      </div>
      <ProjectForm open={edit} onOpenChange={setEdit} project={p} onSaved={(np) => setP({ ...p, ...np })} />
    </div>
  );
}
